import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { X } from "lucide-react";

const FIELDS = [
  ["employee_id", "Employee ID *"], ["full_name", "Full Name *"], ["designation", "Designation *"],
  ["department", "Department"], ["date_of_joining", "Date of Joining (YYYY-MM-DD)"], ["email", "Email"],
  ["location", "Location"], ["employment_type", "Employment Type"], ["photo_url", "Photo URL"],
  ["linkedin_url", "LinkedIn URL"], ["highest_qualification", "Highest Qualification"],
];

export function EmployeeDialog({ emp, chartId, chartEmployees, presetManager, onClose, onSaved }) {
  const [form, setForm] = useState(emp || { manager_employee_id: presetManager || null });
  const isNew = !emp?.id;

  const save = async () => {
    try {
      if (isNew) await api.post("/employees", { ...form, chart_id: chartId, employee_id: form.employee_id || "", full_name: form.full_name || "", designation: form.designation || "" });
      else await api.put(`/employees/${emp.id}`, form);
      toast.success(isNew ? "Person added to chart" : "Person updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="employee-dialog">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-black tracking-tight">{isNew ? "Add person" : `Edit ${emp.full_name}`}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F0F3FF] flex items-center justify-center"><X size={17} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">{label}</label>
              <input data-testid={`emp-field-${k}`} value={form[k] || ""} disabled={!isNew && k === "employee_id"}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full h-9 px-2.5 rounded-lg border border-[#181B34]/15 text-sm font-semibold focus:outline-none focus:border-[#6161FF] disabled:bg-[#F6F7FB]" />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Reports to (within this chart)</label>
            <select data-testid="emp-field-manager" value={form.manager_employee_id || ""}
              onChange={(e) => setForm({ ...form, manager_employee_id: e.target.value || null })}
              className="mt-1 w-full h-9 px-2 rounded-lg border border-[#181B34]/15 text-sm font-semibold bg-white">
              <option value="">— No manager (top of chart) —</option>
              {chartEmployees.filter((e) => e.id !== emp?.id && e.status === "active").map((e) => (
                <option key={e.id} value={e.employee_id}>{e.full_name} — {e.designation}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-xl font-bold text-[#4b4e63] hover:bg-[#F0F3FF]">Cancel</button>
          <button data-testid="save-employee-btn" onClick={save} className="px-5 h-10 rounded-xl bg-[#6161FF] text-white font-bold">{isNew ? "Add person" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}
